/**
 * Environmental Geotech — Zone Boundaries  (Step 1 of the 8-step intelligence loop)
 *
 * Define and manage protected-zone boundaries: Ramsar wetlands, national parks,
 * carbon credit parcels, forest reserves, and geohazard monitoring zones.
 * Supports map-draw (Leaflet Draw) and GeoJSON import.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  PenTool, MapPin, List, Satellite, RefreshCw,
  Plus, Trash2, Pencil, TreePine,
} from 'lucide-react';
import {
  MapContainer, TileLayer, Polygon, Tooltip,
  useMap, FeatureGroup,
} from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import Spinner from '../../components/common/Spinner';
import Modal from '../../components/common/Modal';
import { satelliteBoundariesApi } from '../../services/api';

// Fix leaflet default icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl:       'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl:     'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// ─── Types ────────────────────────────────────────────────────────────────────

interface Zone {
  id: string;
  name: string;
  description?: string;
  area_hectares?: number;
  monitoring_enabled: boolean;
  monitoring_frequency: string;
  created_at: string;
  geometry?: { type: 'Polygon'; coordinates: number[][][] };
  metadata?: {
    zone_type?: string;
    protection_status?: string;
    regulator?: string;
    vertical?: string;
    [key: string]: any;
  };
}

interface ZoneFormState {
  name: string;
  zone_type: string;
  protection_status: string;
  regulator: string;
  monitoring_frequency: string;
}

const EMPTY_FORM: ZoneFormState = {
  name:                '',
  zone_type:           'forest_reserve',
  protection_status:   'protected',
  regulator:           '',
  monitoring_frequency: 'weekly',
};

const ZONE_TYPES = [
  { value: 'forest_reserve',    label: 'Forest Reserve' },
  { value: 'national_park',     label: 'National Park' },
  { value: 'ramsar_wetland',    label: 'Ramsar Wetland' },
  { value: 'carbon_parcel',     label: 'Carbon Credit Parcel' },
  { value: 'geohazard_zone',    label: 'Geohazard Monitoring Zone' },
  { value: 'buffer_zone',       label: 'Buffer / Conservation Zone' },
  { value: 'mining_exclusion',  label: 'Mining Exclusion Zone' },
  { value: 'other',             label: 'Other Protected Zone' },
];

const PROTECTION_STATUSES = [
  { value: 'protected',          label: 'Legally Protected' },
  { value: 'heritage',           label: 'World Heritage' },
  { value: 'ramsar',             label: 'Ramsar (Wetland Convention)' },
  { value: 'carbon_registered',  label: 'Carbon Credit Registered' },
  { value: 'proposed',           label: 'Proposed / Under Review' },
];

const MONITORING_FREQUENCIES = [
  { value: 'daily',    label: 'Daily' },
  { value: 'weekly',   label: 'Weekly' },
  { value: 'monthly',  label: 'Monthly' },
  { value: 'seasonal', label: 'Seasonal' },
];

const ZONE_COLOURS: Record<string, string> = {
  forest_reserve:    '#16a34a',
  national_park:     '#15803d',
  ramsar_wetland:    '#0891b2',
  carbon_parcel:     '#7c3aed',
  geohazard_zone:    '#dc2626',
  buffer_zone:       '#d97706',
  mining_exclusion:  '#9f1239',
  other:             '#6b7280',
};

const CLS = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

// ─── Map auto-fit ─────────────────────────────────────────────────────────────

const FitBounds: React.FC<{ zones: Zone[] }> = ({ zones }) => {
  const map = useMap();
  useEffect(() => {
    const points = zones.flatMap((z) =>
      (z.geometry?.coordinates[0] ?? []).map(([lng, lat]) => [lat, lng] as [number, number])
    );
    if (points.length) {
      try { map.fitBounds(L.latLngBounds(points), { padding: [40, 40] }); } catch {}
    }
  }, [zones, map]);
  return null;
};

function zoneToForm(z: Zone): ZoneFormState {
  return {
    name:                z.name,
    zone_type:           z.metadata?.zone_type            ?? 'forest_reserve',
    protection_status:   z.metadata?.protection_status    ?? 'protected',
    regulator:           z.metadata?.regulator            ?? '',
    monitoring_frequency: z.monitoring_frequency          ?? 'weekly',
  };
}

function hasRealGeometry(z: Zone): boolean {
  return !!(z.geometry?.coordinates?.[0]?.length);
}

// ─── Component ────────────────────────────────────────────────────────────────

export const ZoneBoundariesPage: React.FC = () => {
  const [view,     setView]     = useState<'list' | 'map'>('list');
  const [zones,    setZones]    = useState<Zone[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState<string | null>(null);
  const [success,  setSuccess]  = useState<string | null>(null);

  // form / modal state
  const [showForm,     setShowForm]     = useState(false);
  const [editingId,    setEditingId]    = useState<string | null>(null);
  const [form,         setForm]         = useState<ZoneFormState>(EMPTY_FORM);
  const [drawnGeo,     setDrawnGeo]     = useState<any>(null);
  const [submitting,   setSubmitting]   = useState(false);
  const [deleteId,     setDeleteId]     = useState<string | null>(null);
  const [deleting,     setDeleting]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await satelliteBoundariesApi.getAll();
      const all: Zone[] = res.data?.boundaries ?? [];
      setZones(all.filter((b: any) => b.metadata?.vertical === 'environmental'));
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load zones');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDrawnGeo(null);
    setShowForm(true);
  };

  const openEdit = (z: Zone) => {
    setEditingId(z.id);
    setForm(zoneToForm(z));
    setDrawnGeo(z.geometry ?? null);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { setError('Zone name is required'); return; }
    setSubmitting(true); setError(null);
    const payload = {
      name:                form.name.trim(),
      boundary_type:       'environmental_zone',
      monitoring_enabled:  true,
      monitoring_frequency: form.monitoring_frequency,
      geometry:            drawnGeo ?? undefined,
      metadata: {
        zone_type:         form.zone_type,
        protection_status: form.protection_status,
        regulator:         form.regulator.trim(),
        vertical:          'environmental',
      },
    };
    try {
      if (editingId) {
        await satelliteBoundariesApi.update(editingId, payload);
        setSuccess(`Zone "${form.name}" updated`);
      } else {
        await satelliteBoundariesApi.create(payload);
        setSuccess(`Zone "${form.name}" created`);
      }
      setShowForm(false);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to save zone');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true); setError(null);
    try {
      await satelliteBoundariesApi.deleteById(deleteId);
      const z = zones.find((z) => z.id === deleteId);
      setSuccess(`Zone "${z?.name}" deleted`);
      setDeleteId(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to delete zone');
    } finally {
      setDeleting(false);
    }
  };

  const onDrawCreated = (e: any) => {
    const layer = e.layer;
    const geojson = layer.toGeoJSON();
    setDrawnGeo(geojson.geometry);
  };

  const mappableZones = zones.filter(hasRealGeometry);

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <PenTool className="w-7 h-7 text-green-600" /> Zone Boundaries
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Step 1 — Define protected zones: forest reserves, national parks, Ramsar wetlands, carbon parcels
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setView(view === 'list' ? 'map' : 'list')}>
            {view === 'list' ? <><MapPin className="w-4 h-4 mr-1" /> Map View</> : <><List className="w-4 h-4 mr-1" /> List View</>}
          </Button>
          <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4" /></Button>
          <Button onClick={openCreate}><Plus className="w-4 h-4 mr-1" /> Add Zone</Button>
        </div>
      </div>

      {error   && <Alert type="error"   onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── List View ── */}
      {view === 'list' && (
        <Card padding="none">
          <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {zones.length} protected zone{zones.length !== 1 ? 's' : ''}
            </p>
          </div>
          {loading ? (
            <div className="flex justify-center py-14"><Spinner size="lg" /></div>
          ) : zones.length === 0 ? (
            <div className="text-center py-14">
              <TreePine className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No environmental zones defined</p>
              <p className="text-sm text-gray-400 mt-1">Click <strong>Add Zone</strong> to define your first protected zone</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    {['Name', 'Zone Type', 'Protection Status', 'Regulator', 'Area (ha)', 'Monitoring', 'Actions'].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {zones.map((z) => {
                    const zt = ZONE_TYPES.find((t) => t.value === z.metadata?.zone_type);
                    const ps = PROTECTION_STATUSES.find((t) => t.value === z.metadata?.protection_status);
                    return (
                      <tr key={z.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{z.name}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                            {zt?.label ?? z.metadata?.zone_type ?? '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">{ps?.label ?? z.metadata?.protection_status ?? '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{z.metadata?.regulator || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {z.area_hectares != null ? Number(z.area_hectares).toFixed(1) : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium capitalize ${z.monitoring_enabled ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-100 text-gray-600'}`}>
                            {z.monitoring_enabled ? z.monitoring_frequency : 'Off'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => openEdit(z)}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setDeleteId(z.id)}>
                              <Trash2 className="w-3.5 h-3.5 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* ── Map View ── */}
      {view === 'map' && (
        <Card padding="none">
          <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {mappableZones.length} zone{mappableZones.length !== 1 ? 's' : ''} with geometry
            </p>
          </div>
          <div className="h-[520px] rounded-b-xl overflow-hidden">
            <MapContainer
              center={[20, 0]}
              zoom={2}
              className="h-full w-full"
              scrollWheelZoom
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
              {mappableZones.map((z) => {
                const coords = z.geometry!.coordinates[0].map(([lng, lat]) => [lat, lng] as [number, number]);
                const colour = ZONE_COLOURS[z.metadata?.zone_type ?? 'other'] ?? '#6b7280';
                return (
                  <Polygon
                    key={z.id}
                    positions={coords}
                    pathOptions={{ color: colour, fillColor: colour, fillOpacity: 0.25, weight: 2 }}
                  >
                    <Tooltip sticky>
                      <div className="text-xs">
                        <p className="font-semibold">{z.name}</p>
                        <p>{z.metadata?.zone_type?.replace(/_/g, ' ')}</p>
                        {z.area_hectares != null && <p>{Number(z.area_hectares).toFixed(1)} ha</p>}
                      </div>
                    </Tooltip>
                  </Polygon>
                );
              })}
              <FitBounds zones={mappableZones} />
            </MapContainer>
          </div>
        </Card>
      )}

      {/* ── Create / Edit form modal ── */}
      {showForm && (
        <Modal onClose={() => setShowForm(false)} panelClassName="max-w-2xl p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <TreePine className="w-5 h-5 text-green-600" />
                  {editingId ? 'Edit Zone' : 'New Protected Zone'}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {editingId ? 'Update zone metadata' : 'Define a new environmental monitoring zone'}
                </p>
              </div>
              <Satellite className="w-5 h-5 text-gray-400" />
            </div>

            <div className="space-y-4">
              {/* Zone name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Zone Name *</label>
                <input className={CLS} placeholder="e.g. Sundarbans East Reserve"
                  value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Zone type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Zone Type</label>
                  <select className={CLS} value={form.zone_type}
                    onChange={(e) => setForm((p) => ({ ...p, zone_type: e.target.value }))}>
                    {ZONE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                {/* Protection status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Protection Status</label>
                  <select className={CLS} value={form.protection_status}
                    onChange={(e) => setForm((p) => ({ ...p, protection_status: e.target.value }))}>
                    {PROTECTION_STATUSES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Regulator */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Regulator / Authority</label>
                  <input className={CLS} placeholder="e.g. Ministry of Environment"
                    value={form.regulator} onChange={(e) => setForm((p) => ({ ...p, regulator: e.target.value }))} />
                </div>
                {/* Monitoring frequency */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Monitoring Cadence</label>
                  <select className={CLS} value={form.monitoring_frequency}
                    onChange={(e) => setForm((p) => ({ ...p, monitoring_frequency: e.target.value }))}>
                    {MONITORING_FREQUENCIES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Geometry draw */}
              {!editingId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Draw Zone Boundary (optional)</label>
                  <div className="h-72 rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
                    <MapContainer center={[20, 0]} zoom={2} className="h-full w-full" scrollWheelZoom>
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
                      <FeatureGroup>
                        <EditControl
                          position="topright"
                          onCreated={onDrawCreated}
                          draw={{ rectangle: false, circle: false, circlemarker: false, marker: false, polyline: false }}
                          edit={{ edit: false, remove: false }}
                        />
                      </FeatureGroup>
                    </MapContainer>
                  </div>
                  {drawnGeo && (
                    <p className="mt-1 text-xs text-green-600 dark:text-green-400">✓ Polygon drawn — {drawnGeo.coordinates[0].length - 1} vertices</p>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <Button onClick={handleSubmit} loading={submitting} disabled={!form.name.trim() || submitting} className="flex-1">
                {editingId ? 'Update Zone' : 'Create Zone'}
              </Button>
              <Button variant="outline" onClick={() => setShowForm(false)} disabled={submitting} className="flex-1">
                Cancel
              </Button>
            </div>
        </Modal>
      )}

      {/* ── Delete confirm ── */}
      {deleteId && (
        <Modal onClose={() => setDeleteId(null)} panelClassName="max-w-sm p-6">
            <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">Delete Zone?</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
              <strong>{zones.find((z) => z.id === deleteId)?.name}</strong> will be permanently removed
              along with its monitoring data.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                onClick={handleDelete} loading={deleting}>
                Delete
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => setDeleteId(null)} disabled={deleting}>
                Cancel
              </Button>
            </div>
        </Modal>
      )}
    </div>
  );
};

export default ZoneBoundariesPage;

// Made with Bob
